// The "Filter dashboard" card — a multi-select of active strategies that
// scopes the entire investor page (via InvestorFilterProvider). Replaces
// the mockup's wallet filter; we filter by strategy.
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useInvestorFilter } from '../../lib/investor-filter';

function Box({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
        checked ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300',
      )}
    >
      {checked && <Check className="h-3 w-3" />}
    </span>
  );
}

export function StrategyFilter() {
  const { strategies, isAll, selectedCount, isSelected, toggle, selectAll } = useInvestorFilter();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const label = isAll ? 'All strategies' : `${selectedCount} of ${strategies.length}`;

  return (
    <div ref={ref} className="relative rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-medium text-slate-500">Strategies</p>
      <button
        onClick={() => setOpen((o) => !o)}
        className="mt-2 flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="truncate text-2xl font-bold tracking-tight text-slate-900">{label}</span>
        <ChevronDown
          className={cn('h-5 w-5 shrink-0 text-slate-400 transition-transform', open && 'rotate-180')}
        />
      </button>
      <p className="mt-1 text-sm text-slate-400">Filter dashboard</p>

      {open && (
        <div className="absolute inset-x-0 top-full z-20 mt-2 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
          <button
            onClick={selectAll}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm',
              isAll ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50',
            )}
          >
            <Box checked={isAll} />
            All strategies
          </button>
          <div className="my-1 h-px bg-slate-100" />
          {strategies.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-400">No active strategies</p>
          ) : (
            strategies.map((s) => (
              <button
                key={s.id}
                onClick={() => toggle(s.id)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Box checked={isSelected(s.id)} />
                <span className="flex-1 truncate text-left">{s.displayName}</span>
                <span className="text-xs text-slate-400">{s.baseSymbol}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
